import type {
  INotification,
  INotificationsRequest,
  INotificationsResponse,
  INotificationsState,
  INotificationsType,
  IUpdateNotificationsStatusRequest,
  TNotificationType,
} from '~/interfaces/notifications/notifications';
import { globalKeys } from 'assets/data/global-keys';

type TNotificationFetchStatus = 'FETCHED' | 'NOT_FETCHED' | 'LOADING' | 'ERROR';

const activeTab = ref<TNotificationType>('All');
const loading = ref<boolean>(false);
const error = ref<string | null>(null);
const panelOpen = ref<boolean>(false);
const newNotifications = ref<INotification[]>([]);
const notificationsState = ref<INotificationsState>(null);
const status = ref<TNotificationFetchStatus>('NOT_FETCHED');

export const useNotifications = () => {
  const { $trackEvent, $dayjs, $t } = useNuxtApp();
  const { $marketingService, $storageService } = useServices();
  const site = useSiteStore();

  const maxDate = $dayjs()
    .subtract(6, 'month')
    .startOf('day')
    .utc(true)
    .format();
  const minDate = $dayjs().endOf('day').utc(true).format();

  const DEFAULT_STATE: INotificationsState = {
    allNotifications: { type: 'All', unread: null, notifications: [] },
    marketingNotifications: {
      type: 'Marketing',
      unread: null,
      notifications: [],
    },
    operationalNotifications: {
      type: 'Operational',
      unread: null,
      notifications: [],
    },
    transactionalNotifications: {
      type: 'Transactional',
      unread: null,
      notifications: [],
    },
    hasDateFilter: false,
    total: 0,
    page: 1,
    skip: 0,
    limit: 20,
    startDate: maxDate,
    endDate: minDate,
    maxDate,
    minDate,
  };

  if (status.value === 'NOT_FETCHED')
    notificationsState.value = { ...DEFAULT_STATE };

  const hydrateStateFromResponse = (
    notificationsResponse: INotificationsResponse,
  ) => {
    const deletedNotificationIdsInStorage: string[] | '' =
      $storageService.getSessionStorage({
        key: globalKeys.marketingKeys.deletedNotificationIds,
      });
    if (!!deletedNotificationIdsInStorage)
      notificationsResponse.notifications =
        notificationsResponse.notifications.filter(
          (n) => !deletedNotificationIdsInStorage.includes(n.notificationId),
        );

    newNotifications.value = [];
    notificationsState.value = {
      ...notificationsState.value,
      allNotifications: {
        type: 'All',
        unread: notificationsResponse.notifications.filter((n) => !n.isRead)
          .length,
        notifications: notificationsResponse.notifications,
      },
      marketingNotifications: {
        type: 'Marketing',
        unread: notificationsResponse.notifications.filter(
          (n) => n.notificationType === 'Marketing' && !n.isRead,
        ).length,
        notifications: notificationsResponse.notifications.filter(
          (n) => n.notificationType === 'Marketing',
        ),
      },
      operationalNotifications: {
        type: 'Operational',
        unread: notificationsResponse.notifications.filter(
          (n) => n.notificationType === 'Operational' && !n.isRead,
        ).length,
        notifications: notificationsResponse.notifications.filter(
          (n) => n.notificationType === 'Operational',
        ),
      },
      transactionalNotifications: {
        type: 'Transactional',
        unread: notificationsResponse.notifications.filter(
          (n) => n.notificationType === 'Transactional' && !n.isRead,
        ).length,
        notifications: notificationsResponse.notifications.filter(
          (n) => n.notificationType === 'Transactional',
        ),
      },
      total: notificationsResponse.total,
      // TODO: refactor below fields when we have the oldest notification date available in the API response
      maxDate,
      minDate,
    };
  };

  const fetchNotifications = async ({
    skip,
    limit,
    startDate,
    endDate,
    overwriteStorage = true,
  }: INotificationsRequest = {}) => {
    try {
      const notificationsInStorage =
        $storageService.getSessionStorage<INotificationsResponse>({
          key: globalKeys.marketingKeys.notifications,
        });

      if (!!notificationsInStorage && !overwriteStorage) {
        hydrateStateFromResponse(notificationsInStorage);
        status.value = 'FETCHED';
        return notificationsInStorage;
      }

      $storageService.removeSessionStorage({
        key: globalKeys.marketingKeys.notifications,
      });

      loading.value = true;
      status.value = 'LOADING';
      const notificationsResponse = await $marketingService.fetchNotifications({
        ...(!!skip && { skip }),
        ...(!!limit && { limit }),
        ...(!!startDate && { startDate }),
        ...(!!endDate && { endDate }),
      });

      if (!notificationsResponse) {
        status.value = 'ERROR';
        throw new Error('notifications-general-error');
      }

      $storageService.setSessionStorage({
        key: globalKeys.marketingKeys.notifications,
        data: notificationsResponse,
      });

      hydrateStateFromResponse(notificationsResponse);
      status.value = 'FETCHED';
      return notificationsResponse;
    } catch (e) {
      error.value = e?.message || 'notifications-general-error';
      status.value = 'ERROR';
    } finally {
      loading.value = false;
    }
  };

  const fetchNotificationsOnOpen = async () => {
    const notificationsInStorage =
      $storageService.getSessionStorage<INotificationsResponse>({
        key: globalKeys.marketingKeys.notifications,
      });

    if (!!notificationsInStorage) {
      hydrateStateFromResponse(notificationsInStorage);
      status.value = 'FETCHED';
    } else {
      await fetchNotifications({ overwriteStorage: true });
    }
  };

  const updateByIds = async ({
    isRead,
    ids,
    updateType,
  }: IUpdateNotificationsStatusRequest) => {
    try {
      if (updateType === 'Delete') {
        loading.value = true;
        status.value = 'LOADING';
        const deletedNotificationIdsInStorage: string[] | '' =
          $storageService.getSessionStorage({
            key: globalKeys.marketingKeys.deletedNotificationIds,
          });
        if (!!deletedNotificationIdsInStorage) {
          const updatedNotificationIdsList = [
            ...deletedNotificationIdsInStorage,
            ...ids,
          ];
          $storageService.setSessionStorage({
            key: globalKeys.marketingKeys.deletedNotificationIds,
            data: updatedNotificationIdsList,
          });
        } else
          $storageService.setSessionStorage({
            key: globalKeys.marketingKeys.deletedNotificationIds,
            data: ids,
          });
      }
      await $marketingService.updateNotificationStatusByIds({
        isRead,
        ids,
        updateType,
      });
      assessNotifications(updateType, ids, isRead);
      syncStorageByIds(updateType, ids, isRead);
      status.value = 'FETCHED';
    } catch (e) {
      status.value = 'ERROR';
      error.value = e?.message || 'notifications-general-error';
    } finally {
      loading.value = false;
    }
  };

  const makeIdSet = (ids: string[]) => new Set(ids);
  const notById = (ids: string[]) => {
    const set = makeIdSet(ids);
    return (n: INotification) => !set.has(n.notificationId);
  };
  const setReadFlag = (ids: string[], isRead?: boolean) => {
    const set = makeIdSet(ids);
    return (n: INotification): INotification =>
      set.has(n.notificationId) ? { ...n, isRead: !!isRead } : n;
  };
  const countUnread = (items: INotification[]) =>
    items.reduce((acc, n) => acc + (n.isRead ? 0 : 1), 0);
  const syncStorageByIds = (
    type: 'Delete' | 'Status',
    ids: string[],
    isRead?: boolean,
  ) => {
    const notificationsInStorage =
      $storageService.getSessionStorage<INotificationsResponse>({
        key: globalKeys.marketingKeys.notifications,
      });
    if (!notificationsInStorage) return;

    const updatedNotifications =
      type === 'Delete'
        ? notificationsInStorage.notifications.filter(notById(ids))
        : notificationsInStorage.notifications.map(setReadFlag(ids, isRead));

    $storageService.setSessionStorage({
      key: globalKeys.marketingKeys.notifications,
      data: {
        ...notificationsInStorage,
        notifications: updatedNotifications,
        total:
          type === 'Delete'
            ? Math.max(
                0,
                notificationsInStorage.total -
                  (notificationsInStorage.notifications.length -
                    updatedNotifications.length),
              )
            : notificationsInStorage.total,
        unreadNotificationsCount: countUnread(updatedNotifications),
      },
    });
  };
  const assessNotifications = (
    type: 'Delete' | 'Status' | 'Add',
    ids: string[],
    isRead?: boolean,
  ) => {
    const updatedAll =
      type === 'Delete'
        ? notificationsState.value.allNotifications.notifications.filter(
            notById(ids),
          )
        : type === 'Status'
          ? notificationsState.value.allNotifications.notifications.map(
              setReadFlag(ids, isRead),
            )
          : [
              newNotifications.value[0],
              ...notificationsState.value.allNotifications.notifications,
            ];

    const operational = updatedAll.filter(
      (n) => n.notificationType === 'Operational',
    );
    const marketing = updatedAll.filter(
      (n) => n.notificationType === 'Marketing',
    );
    const transactional = updatedAll.filter(
      (n) => n.notificationType === 'Transactional',
    );

    notificationsState.value = {
      ...notificationsState.value,
      allNotifications: {
        type: 'All',
        notifications: updatedAll,
        unread: countUnread(updatedAll),
      },
      operationalNotifications: {
        type: 'Operational',
        notifications: operational,
        unread: countUnread(operational),
      },
      marketingNotifications: {
        type: 'Marketing',
        notifications: marketing,
        unread: countUnread(marketing),
      },
      transactionalNotifications: {
        type: 'Transactional',
        notifications: transactional,
        unread: countUnread(transactional),
      },
      ...(type === 'Delete' && {
        total: notificationsState.value.total - ids.length,
      }),
    };
  };

  const paginateForward = async (id: string) => {
    $trackEvent('Notification', 'NOTIFICATION_PANEL_PAGINATE_FORWARD', {
      activeTab: activeTab.value,
      startDate: notificationsState.value.startDate,
      endDate: notificationsState.value.endDate,
      currentPage: notificationsState.value.page,
      nextPage: notificationsState.value.page + 1,
    });
    notificationsState.value = {
      ...notificationsState.value,
      skip: notificationsState.value.skip + notificationsState.value.limit,
      page: notificationsState.value.page + 1,
    };
    await fetchNotifications({
      skip: notificationsState.value.skip,
      limit: notificationsState.value.limit,
      startDate: notificationsState.value.startDate,
      endDate: notificationsState.value.endDate,
      overwriteStorage: true,
    });

    document.getElementById(id)?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const paginateBackward = async (id: string) => {
    $trackEvent('Notification', 'NOTIFICATION_PANEL_PAGINATE_BACKWARD', {
      activeTab: activeTab.value,
      startDate: notificationsState.value.startDate,
      endDate: notificationsState.value.endDate,
      currentPage: notificationsState.value.page,
      nextPage: notificationsState.value.page - 1,
    });
    notificationsState.value = {
      ...notificationsState.value,
      skip: notificationsState.value.skip - notificationsState.value.limit,
      page: notificationsState.value.page - 1,
    };
    await fetchNotifications({
      skip: notificationsState.value.skip,
      limit: notificationsState.value.limit,
      startDate: notificationsState.value.startDate,
      endDate: notificationsState.value.endDate,
      overwriteStorage: true,
    });
    document.getElementById(id)?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetNotificationState = async (refetch = false) => {
    notificationsState.value = { ...DEFAULT_STATE };
    if (refetch) {
      await fetchNotifications({ overwriteStorage: true });
      notificationsState.value.skip = 0;
      notificationsState.value.page = 1;
    }
  };

  const persistNewNotificationToStorage = (notification: INotification) => {
    const notificationsInStorage =
      $storageService.getSessionStorage<INotificationsResponse>({
        key: globalKeys.marketingKeys.notifications,
      });

    const base: INotificationsResponse = notificationsInStorage || {
      unreadNotificationsCount: 0,
      notifications: [],
      total: 0,
      offset: 0,
    };

    if (
      base.notifications.some(
        (n) => n.notificationId === notification.notificationId,
      )
    )
      return;

    $storageService.setSessionStorage({
      key: globalKeys.marketingKeys.notifications,
      data: {
        ...base,
        notifications: [notification, ...base.notifications],
        total: base.total + 1,
        unreadNotificationsCount:
          base.unreadNotificationsCount + (notification.isRead ? 0 : 1),
      },
    });
  };

  const handleNewNotification = (notification: INotification) => {
    const route = useRoute();
    if (
      !newNotifications.value.some(
        (n) => n.notificationId === notification.notificationId,
      )
    ) {
      newNotifications.value = [notification, ...newNotifications.value];
      persistNewNotificationToStorage(notification);
      assessNotifications('Add', []);
      if (
        !panelOpen.value &&
        newNotifications.value.length > 0 &&
        !route.params?.game
      )
        site.activateModal('newNotification');
    }
  };

  const timeSinceNotification = (notificationDate: Date | string | number) => {
    const now = $dayjs();
    const then = $dayjs(notificationDate);

    if (!then.isValid()) return $t('just-now', 'Just Now');

    const diffMs = now.diff(then);
    const d = $dayjs.duration(diffMs);

    const months = Math.floor(d.asMonths());
    const hoursTotal = Math.floor(d.asHours());
    const daysBase = Math.floor(d.asDays());
    const hoursRemainder = hoursTotal - daysBase * 24;

    const days = Math.floor(hoursRemainder > 12 ? daysBase + 1 : daysBase);
    const hours = Math.floor(d.asHours() - daysBase * 24);
    const minutes = Math.floor(d.asMinutes() - hoursTotal * 60);

    if (months > 0) {
      return `${months} ${$t(`month${months > 1 ? 's' : ''}`, `Month${months > 1 ? 's' : ''}`)} ${$t('ago', 'Ago')}`;
    } else if (days > 0) {
      return `${days} ${$t(`day${days > 1 ? 's' : ''}`, `Day${days > 1 ? 's' : ''}`)} ${$t('ago', 'Ago')}`;
    } else if (hours > 0) {
      return `${hours} ${$t(`hour${hours > 1 ? 's' : ''}`, `Hour${hours > 1 ? 's' : ''}`)} ${$t('ago', 'Ago')}`;
    } else if (minutes > 0) {
      return `${minutes} ${$t(`minute${minutes > 1 ? 's' : ''}`, `Minute${minutes > 1 ? 's' : ''}`)} ${$t('ago', 'Ago')}`;
    }
    return $t('just-now', 'Just Now');
  };

  return {
    status: computed(() => status.value) as Readonly<
      Ref<TNotificationFetchStatus>
    >,
    activeTab: computed({
      get: () => activeTab.value,
      set: (value) => {
        $trackEvent('Notification', 'NOTIFICATION_PANEL_CHANGE_TAB', {
          activeTab: value,
        });
        return (activeTab.value = value);
      },
    }),
    availableNotifications: computed(() => {
      return [
        notificationsState.value.allNotifications,
        notificationsState.value.marketingNotifications.notifications.length > 0
          ? notificationsState.value.marketingNotifications
          : null,
        notificationsState.value.operationalNotifications.notifications.length >
        0
          ? notificationsState.value.operationalNotifications
          : null,
        notificationsState.value.transactionalNotifications.notifications
          .length > 0
          ? notificationsState.value.transactionalNotifications
          : null,
      ].filter((n) => !!n);
    }) as Readonly<Ref<INotificationsType[]>>,
    error: computed(() => error.value) as Readonly<Ref<string | null>>,
    fetchNotifications,
    fetchNotificationsOnOpen,
    handleNewNotification,
    loading: computed(() => loading.value) as Readonly<Ref<boolean>>,
    notificationsState: computed({
      get: () => notificationsState.value,
      set: (value) => {
        notificationsState.value = {
          ...notificationsState.value,
          ...value,
        };
      },
    }),
    newNotifications,
    paginateForward,
    paginateBackward,
    panelOpen: computed({
      get: () => panelOpen.value,
      set: (value) => {
        if (value) $trackEvent('Notification', 'NOTIFICATION_OPEN_PANEL');
        else $trackEvent('Notification', 'NOTIFICATION_CLOSE_PANEL');
        panelOpen.value = value;
      },
    }),
    resetNotificationState,
    updateByIds,
    timeSinceNotification,
  } as const;
};
